import React, { useEffect, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import { Row, Col, Button, Input, Card, CardBody, FormGroup, Table, 
    Modal, ModalHeader, ModalBody, ModalFooter, Form, Label, Badge } from 'reactstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { withTranslation } from 'react-multi-lang';
import {
    faPen,
    faPlus,
    faTrash,
    faRefresh,
    faGripVertical,
    faSyncAlt
} from "@fortawesome/free-solid-svg-icons";
import EssService from '../Services/EssService';

const EssSettings = ({ t }) => {
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [specialties, setSpecialties] = useState([]);

    // Modals for editing/creating
    const [leaveTypeModalOpen, setLeaveTypeModalOpen] = useState(false);
    const [specialtyModalOpen, setSpecialtyModalOpen] = useState(false);

    const [isEditLeaveType, setIsEditLeaveType] = useState(false);
    const [isEditSpecialty, setIsEditSpecialty] = useState(false);

    const [currentLeaveType, setCurrentLeaveType] = useState({ id: null, name: '', daysLimit: '', isSpecial: false, description: '' });
    const [currentSpecialty, setCurrentSpecialty] = useState({ id: null, name: '' });

    const essService = new EssService();
    const { addToast } = useToasts();

    useEffect(() => {
        loadAll();
    }, []);

    const loadAll = () => {
        loadLeaveTypes();
        loadSpecialties();
    };

    const loadLeaveTypes = () => {
        essService.getLeaveTypes()
            .then(res => setLeaveTypes(res.data || []))
            .catch(e => addToast(e.message || t('ess.errorLoadingLeaveTypes'), { appearance: 'error', autoDismiss: true }));
    };

    const loadSpecialties = () => {
        essService.getSpecialties()
            .then(res => setSpecialties(res.data || []))
            .catch(e => addToast(e.message || t('ess.errorLoadingSpecialties'), { appearance: 'error', autoDismiss: true }));
    };

    // --- Leave Types Modal & Save ---
    const toggleLeaveTypeModal = () => {
        if (leaveTypeModalOpen) {
            setCurrentLeaveType({ id: null, name: '', daysLimit: '', isSpecial: false, description: '' });
            setIsEditLeaveType(false);
        }
        setLeaveTypeModalOpen(!leaveTypeModalOpen);
    };

    const openAddLeaveType = () => {
        setCurrentLeaveType({ id: null, name: '', daysLimit: '', isSpecial: false, description: '' });
        setIsEditLeaveType(false);
        setLeaveTypeModalOpen(true);
    };

    const openEditLeaveType = (lt) => {
        setCurrentLeaveType({
            id: lt.id,
            name: lt.name,
            daysLimit: lt.daysLimit !== null && lt.daysLimit !== undefined ? lt.daysLimit : '',
            isSpecial: !!lt.isSpecial || (lt.daysLimit !== null && lt.daysLimit !== undefined && lt.daysLimit > 0),
            description: lt.description || ''
        });
        setIsEditLeaveType(true);
        setLeaveTypeModalOpen(true);
    };

    const handleSaveLeaveType = (e) => {
        e.preventDefault();
        const daysLimitVal = currentLeaveType.daysLimit !== '' && currentLeaveType.daysLimit !== null && !isNaN(Number(currentLeaveType.daysLimit))
            ? parseInt(currentLeaveType.daysLimit, 10)
            : null;
        const isSpecialVal = currentLeaveType.isSpecial || (daysLimitVal !== null && daysLimitVal > 0);

        const promise = isEditLeaveType 
            ? essService.updateLeaveType(currentLeaveType.id, currentLeaveType.name, daysLimitVal, isSpecialVal, currentLeaveType.description)
            : essService.createLeaveType(currentLeaveType.name, daysLimitVal, isSpecialVal, currentLeaveType.description);

        promise.then(() => {
            addToast(t('ess.successLeaveSaved'), { appearance: 'success', autoDismiss: true });
            toggleLeaveTypeModal();
            loadLeaveTypes();
        }).catch(e => {
            addToast(e.message || t('ess.errorSavingLeaveType'), { appearance: 'error', autoDismiss: true });
        });
    };

    const handleSyncLeaveTypes = () => {
        essService.syncLeaveTypes()
            .then(() => {
                addToast(t('ess.successLeaveTypesSynced') || 'Leave types synced successfully!', { appearance: 'success', autoDismiss: true });
                loadLeaveTypes();
            })
            .catch(e => {
                addToast(e.message || t('ess.errorSyncingLeaveTypes') || 'Error syncing leave types.', { appearance: 'error', autoDismiss: true });
            });
    };

    const handleDeleteLeaveType = (id) => {
        if (window.confirm(t('ess.confirmDeleteLeave'))) {
            essService.deleteLeaveType(id).then(() => {
                addToast(t('ess.successLeaveDeleted'), { appearance: 'success', autoDismiss: true });
                loadLeaveTypes();
            }).catch(e => {
                addToast(e.message || t('ess.errorDeletingLeaveType'), { appearance: 'error', autoDismiss: true });
            });
        }
    };

    // --- Drag and Drop for Leave Types ---
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/html', e.currentTarget);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnter = (e, index) => {
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e, targetIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === targetIndex) return;

        const updatedList = [...leaveTypes];
        const draggedItem = updatedList[draggedIndex];
        
        updatedList.splice(draggedIndex, 1);
        updatedList.splice(targetIndex, 0, draggedItem);

        setLeaveTypes(updatedList);

        const ids = updatedList.map(item => item.id);
        essService.reorderLeaveTypes(ids)
            .then(() => {
                addToast(t('ess.successReorderingLeaveTypes'), { appearance: 'success', autoDismiss: true });
            })
            .catch(err => {
                addToast(err.message || t('ess.errorReorderingLeaveTypes'), { appearance: 'error', autoDismiss: true });
                loadLeaveTypes();
            });
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const getRowStyle = (index) => {
        let style = { cursor: 'grab' };
        if (index === draggedIndex) {
            style.opacity = 0.5;
        }
        if (index === dragOverIndex && index !== draggedIndex) {
            style.backgroundColor = '#e9ecef';
        }
        return style;
    };

    // --- Specialties Modal & Save ---
    const toggleSpecialtyModal = () => {
        if (specialtyModalOpen) {
            setCurrentSpecialty({ id: null, name: '' });
            setIsEditSpecialty(false);
        }
        setSpecialtyModalOpen(!specialtyModalOpen);
    };

    const openAddSpecialty = () => {
        setCurrentSpecialty({ id: null, name: '' });
        setIsEditSpecialty(false);
        setSpecialtyModalOpen(true);
    };

    const openEditSpecialty = (spec) => {
        setCurrentSpecialty({ id: spec.id, name: spec.name });
        setIsEditSpecialty(true);
        setSpecialtyModalOpen(true);
    };

    const handleSaveSpecialty = (e) => {
        e.preventDefault();
        const promise = isEditSpecialty 
            ? essService.updateSpecialty(currentSpecialty.id, currentSpecialty.name)
            : essService.createSpecialty(currentSpecialty.name);

        promise.then(() => {
            addToast(t('ess.successSpecialtySaved'), { appearance: 'success', autoDismiss: true });
            toggleSpecialtyModal();
            loadSpecialties();
        }).catch(e => {
            addToast(e.message || t('ess.errorSavingSpecialty'), { appearance: 'error', autoDismiss: true });
        });
    };

    const handleDeleteSpecialty = (id) => {
        if (window.confirm(t('ess.confirmDeleteSpecialty'))) {
            essService.deleteSpecialty(id).then(() => {
                addToast(t('ess.successSpecialtyDeleted'), { appearance: 'success', autoDismiss: true });
                loadSpecialties();
            }).catch(e => {
                addToast(e.message || t('ess.errorDeletingSpecialty'), { appearance: 'error', autoDismiss: true });
            });
        }
    };

    return (
        <Card className="shadow-sm">
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4>{t('ess.settingsTitle')}</h4>
                    <Button color="secondary" size="sm" onClick={loadAll}>
                        <FontAwesomeIcon icon={faRefresh} className="mr-2" /> {t('ess.refresh')}
                    </Button>
                </div>

                <Row>
                    {/* Leave Types Management Column */}
                    <Col md="6" className="mb-4">
                        <Card className="border p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5>{t('ess.leaveTypes')}</h5>
                                <div>
                                    <Button color="secondary" size="sm" className="mr-2 me-2" onClick={handleSyncLeaveTypes} title={t('ess.syncLeaveTypes')}>
                                        <FontAwesomeIcon icon={faSyncAlt} className="mr-1" /> {t('ess.syncLeaveTypes') || 'Sync Types'}
                                    </Button>
                                    <Button color="primary" size="sm" onClick={openAddLeaveType}>
                                        <FontAwesomeIcon icon={faPlus} className="mr-1" /> {t('ess.addType')}
                                    </Button>
                                </div>
                            </div>
                            <Table responsive striped hover size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}></th>
                                        <th>{t('ess.id')}</th>
                                        <th>{t('ess.name')}</th>
                                        <th>{t('ess.yearlyLimit') || 'Limit (Days)'}</th>
                                        <th>{t('ess.isSpecial') || 'Type'}</th>
                                        <th className="text-right">{t('ess.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leaveTypes.map((lt, index) => (
                                        <tr 
                                            key={lt.id}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragOver={(e) => handleDragOver(e, index)}
                                            onDragEnter={(e) => handleDragEnter(e, index)}
                                            onDragLeave={handleDragLeave}
                                            onDrop={(e) => handleDrop(e, index)}
                                            onDragEnd={handleDragEnd}
                                            style={getRowStyle(index)}
                                        >
                                            <td className="text-muted text-center" style={{ cursor: 'grab' }}>
                                                <FontAwesomeIcon icon={faGripVertical} />
                                            </td>
                                            <td>{lt.id}</td>
                                            <td>
                                                <strong>{lt.name}</strong>
                                                {lt.description && <div className="text-muted small">{lt.description}</div>}
                                            </td>
                                            <td>
                                                {lt.daysLimit !== null && lt.daysLimit !== undefined ? (
                                                    <Badge color="info">{lt.daysLimit} {t('ess.days') || 'days'}</Badge>
                                                ) : '-'}
                                            </td>
                                            <td>
                                                {lt.isSpecial || (lt.daysLimit !== null && lt.daysLimit !== undefined) ? (
                                                    <Badge color="warning">{t('ess.specialLeave') || 'Special'}</Badge>
                                                ) : (
                                                    <Badge color="success">{t('ess.regularLeave') || 'Regular'}</Badge>
                                                )}
                                            </td>
                                            <td className="text-right">
                                                <Button color="info" size="sm" className="mr-1 me-1" onClick={() => openEditLeaveType(lt)}>
                                                    <FontAwesomeIcon icon={faPen} />
                                                </Button>
                                                <Button color="danger" size="sm" onClick={() => handleDeleteLeaveType(lt.id)}>
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {leaveTypes.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center">{t('ess.noLeaveTypes')}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card>
                    </Col>

                    {/* Specialties Management Column */}
                    <Col md="6" className="mb-4">
                        <Card className="border p-3">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5>{t('ess.employeeSpecialties')}</h5>
                                <Button color="primary" size="sm" onClick={openAddSpecialty}>
                                    <FontAwesomeIcon icon={faPlus} className="mr-1" /> {t('ess.addSpecialty')}
                                </Button>
                            </div>
                            <Table responsive striped hover size="sm">
                                <thead>
                                    <tr>
                                        <th>{t('ess.id')}</th>
                                        <th>{t('ess.name')}</th>
                                        <th className="text-right">{t('ess.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {specialties.map(spec => (
                                        <tr key={spec.id}>
                                            <td>{spec.id}</td>
                                            <td>{spec.name}</td>
                                            <td className="text-right">
                                                <Button color="info" size="sm" className="mr-1" onClick={() => openEditSpecialty(spec)}>
                                                    <FontAwesomeIcon icon={faPen} />
                                                </Button>
                                                <Button color="danger" size="sm" onClick={() => handleDeleteSpecialty(spec.id)}>
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    {specialties.length === 0 && (
                                        <tr>
                                            <td colSpan="3" className="text-center">{t('ess.noSpecialties')}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card>
                    </Col>
                </Row>
            </CardBody>

            {/* Leave Type Modal */}
            <Modal isOpen={leaveTypeModalOpen} toggle={toggleLeaveTypeModal}>
                <Form onSubmit={handleSaveLeaveType}>
                    <ModalHeader toggle={toggleLeaveTypeModal}>
                        {isEditLeaveType ? t('ess.editLeaveType') : t('ess.addLeaveType')}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>{t('ess.name')}</Label>
                            <Input 
                                type="text" 
                                required
                                value={currentLeaveType.name}
                                onChange={(e) => setCurrentLeaveType({ ...currentLeaveType, name: e.target.value })}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('ess.yearlyLimit') || 'Yearly Limit (Working Days)'}</Label>
                            <Input 
                                type="number"
                                min="0"
                                placeholder={t('ess.daysLimitPlaceholder') || 'e.g. 5 (leave blank for unlimited / annual)'}
                                value={currentLeaveType.daysLimit}
                                onChange={(e) => setCurrentLeaveType({ ...currentLeaveType, daysLimit: e.target.value })}
                            />
                            <small className="text-muted">{t('ess.yearlyLimitHelp') || 'Maximum allowed working days per year for this leave type.'}</small>
                        </FormGroup>
                        <FormGroup check className="mb-3">
                            <Label check>
                                <Input 
                                    type="checkbox"
                                    checked={currentLeaveType.isSpecial}
                                    onChange={(e) => setCurrentLeaveType({ ...currentLeaveType, isSpecial: e.target.checked })}
                                />{' '}
                                {t('ess.isSpecial') || 'Special Leave (does not deduct from annual leave allowance)'}
                            </Label>
                        </FormGroup>
                        <FormGroup>
                            <Label>{t('ess.description') || 'Description / Justification Details'}</Label>
                            <Input 
                                type="text"
                                placeholder="e.g. ΓΑΜΟΣ (5 ΕΡΓΑΣΙΜΕΣ)"
                                value={currentLeaveType.description}
                                onChange={(e) => setCurrentLeaveType({ ...currentLeaveType, description: e.target.value })}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" type="submit">{t('ess.save')}</Button>
                        <Button color="secondary" onClick={toggleLeaveTypeModal}>{t('ess.cancel')}</Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Specialty Modal */}
            <Modal isOpen={specialtyModalOpen} toggle={toggleSpecialtyModal}>
                <Form onSubmit={handleSaveSpecialty}>
                    <ModalHeader toggle={toggleSpecialtyModal}>
                        {isEditSpecialty ? t('ess.editSpecialty') : t('ess.addSpecialty')}
                    </ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>{t('ess.name')}</Label>
                            <Input 
                                type="text" 
                                required
                                value={currentSpecialty.name}
                                onChange={(e) => setCurrentSpecialty({ ...currentSpecialty, name: e.target.value })}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" type="submit">{t('ess.save')}</Button>
                        <Button color="secondary" onClick={toggleSpecialtyModal}>{t('ess.cancel')}</Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </Card>
    );
};

export default withTranslation(EssSettings);
